import React, { FC, ReactElement, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AxiosError, AxiosResponse } from 'axios'
import { Formik, FormikProps } from 'formik'

import PhoneInputWrapper from './components/PhoneInputWrapper/PhoneInputWrapper'
import CodeInput from '../CodeInput/CodeInput'

import { actions as userActions, phoneAuthConfirm } from '@store/modules/user/actions'
import { setLoaded } from '@store/modules/startup/actions'
import usersService from '@services/users'

import validationSchema from './validationSchema'

import { phoneFieldNumberRegexp, ResponseCode, SigninFormMode } from '@const/consts'
import { IApplicationState } from '@store/types/commonTypes'
import { IPhoneFormState as IState, IPhoneFormProps as IProps } from './types'

const initialValues: IState = {
  phone: null,
  code: null,
  remember: false
}

const PhoneForm: FC<IProps> = (props) => {
  const { t } = useTranslation('notification')
  const { isSending, onSetMode, history, location: { state }, onServerErrorChange } = props

  const dispatch = useDispatch<any>()
  const isShowPhoneInput = useSelector((state: IApplicationState) => state.user.isShowPhoneInput)
  const phoneAuthId = useSelector((state: IApplicationState) => state.user.phoneAuthId)

  useEffect(() => onServerErrorChange(''), [isShowPhoneInput])

  useEffect(() => {
    return () => {
      dispatch(userActions.setIsShowPhoneInput(true))
    }
  }, [])

  const renderForm = (props: FormikProps<IState>): ReactElement => {
    const { handleSubmit, values, setValues } = props

    const handleClick = (): void => {
      if (values.phone && isShowPhoneInput) {
        usersService.phoneVerify({ phone: Number(values.phone.replace(phoneFieldNumberRegexp, '')) })
          .then((resp: AxiosResponse) => {
            if (resp.status === ResponseCode.POST) {
              dispatch(userActions.setPhoneAuthId(resp.data.id))
              dispatch(userActions.setIsShowPhoneInput(false))
            }

            return resp
          })
          .catch((err) => {
            dispatch(userActions.setIsShowPhoneInput(true))
            const errMessage = err.response?.data?.message
            onServerErrorChange(errMessage ?? t('notification:errorTitle'))
          })
      }
    }

    const handleChangeNumberClick = (): void => {
      dispatch(userActions.setIsShowPhoneInput(true))
      setValues({
        ...values,
        phone: `+${values.phone?.replace(phoneFieldNumberRegexp, '')}`
      })
    }

    const handleEmailLoginClick = (): void => {
      onSetMode(SigninFormMode.EMAIL)
    }

    const handleSSOLoginClick = (): void => {
      onSetMode(SigninFormMode.SSO)
      onServerErrorChange
    }

    return (
      <form onSubmit={handleSubmit} noValidate>
        {isShowPhoneInput ? (
          <PhoneInputWrapper
            {...props}
            onEmailLoginClick={handleEmailLoginClick}
            onContinueBtnClick={handleClick}
            onSSOLoginClick={handleSSOLoginClick}
            onServerErrorChange={onServerErrorChange}
          />
        ) : (
          <CodeInput
            {...props}
            isSending={isSending}
            onNavigationClick={handleEmailLoginClick}
            onChangeNumberClick={handleChangeNumberClick}
            onSSOLoginClick={handleSSOLoginClick}
            onServerErrorChange={onServerErrorChange}
          />
        )}
      </form>
    )
  }

  const handleSubmit = ({ remember, code }: IState): void => {
    dispatch(phoneAuthConfirm({ id: phoneAuthId, code }, remember))
      .then((resp: AxiosResponse) => {
        if (resp.status === ResponseCode.POST) {
          dispatch(setLoaded(false))
          state?.from && history.push(state?.from)

          history.push("/")
        }
      })
      .catch((err: AxiosError<any>) => {
        const errMessage = err.response?.data?.message
        onServerErrorChange(errMessage ?? t('notification:errorTitle'))
      })
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {renderForm}
    </Formik>
  )
}

export default PhoneForm
